import { useEffect, useRef, useState, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Container, Text, useTranslation, useUi } from '@weareconceptstudio/core';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getSmoother } from '@/components/ScrollWrapper/gsapSmootherInstance';

//* Styles
import ListStyle from './style';

//* Components
import RecipesItem from './Item';
import Button from '@/components/Button';
import Filter from './Filter';

//* Helpers Anim
import { useScrollRestore } from '@/modules/_helpersHOOK';
import { groupByRows } from '@/modules/_animation/helpers';

//* Show More Config
const INITIAL_VISIBLE = 28;
const ITEMS_PER_SHOW = 4;

const List = ({ data }) => {
	const pathname = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();

	const { isMobile, winWidth } = useUi();

	const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
	const totalItems = data.length;

	//! Ref
	const recipesWrapRef = useRef();
	const triggerRef = useRef([]);
	const styleRef = useRef();
	const groupRef = useRef([]);

	const parseSearchParamsToSelected = (searchParams) => {
		const parsed = {};

		searchParams.forEach((value, key) => {
			parsed[key] = value.split(',').filter(Boolean);
		});

		return parsed;
	};

	const initialSelected = useMemo(() => parseSearchParamsToSelected(searchParams), [searchParams]);
	const [selected, setSelected] = useState(initialSelected); // lift state

	const handleShowMore = () => {
		setVisibleCount((prevCount) => Math.min(prevCount + ITEMS_PER_SHOW, totalItems));
	};

	const visibleItems = data.slice(0, visibleCount);

	const handleClearFilters = () => {
		setSelected({});
		router.push(pathname);
	};

	//! Ref
	const recipesRefWrapper = useRef();
	const scrollRef = useRef();
	const firstLoad = useRef(true);

	useScrollRestore({ dep: [searchParams], working: !firstLoad.current });

	useEffect(() => {
		gsap.to(recipesRefWrapper.current, {
			opacity: 1,
			duration: 0.6,
			ease: 'power2.in',
			delay: 0.2,
		});
		if (recipesRefWrapper.current && !firstLoad.current) {
			const smoother = getSmoother();

			if (smoother && !isMobile) {
				smoother.paused(true);
				const headerHeight = document?.querySelector('header')?.getBoundingClientRect()?.height || 0;
				const targetY = recipesRefWrapper.current.getBoundingClientRect()?.top + smoother?.scrollTop() - headerHeight;

				if (scrollRef.current) {
					gsap.killTweensOf(scrollRef.current);
				}

				smoother.paused(false);
				scrollRef.current = gsap.to(smoother, {
					scrollTop: targetY,
					duration: 0.6,
					ease: 'power2.out',
					delay: 0.15,
				});
			} else {
				const headerHeight = document?.querySelector('header')?.getBoundingClientRect()?.height || 0;
				setTimeout(() => {
					const targetY = recipesRefWrapper.current.getBoundingClientRect().top + window.scrollY - headerHeight;
					window.scrollTo({
						top: targetY,
						behavior: 'smooth',
					});
				}, 100);
			}
		}
		firstLoad.current = false;
	}, [searchParams, pathname, isMobile]);

	useEffect(() => {
		if (recipesWrapRef.current?.children) {
			groupRef.current = groupByRows(recipesWrapRef.current);

			if (groupRef.current?.length) {
				groupRef.current.forEach((item, index) => {
					item.forEach((el, ind) => {
						gsap.set(el, {
							opacity: 0,
							y: 0,
						});
					});

					triggerRef.current[index] = ScrollTrigger.create({
						start: 'top bottom',
						end: 'top top',
						// markers: true,
						trigger: item,
						onEnter: () => {
							gsap.fromTo(item, { opacity: 0 }, { opacity: 1, duration: 0.7, ease: 'power2.out', stagger: 0.05 });
							gsap.fromTo(
								item,
								{
									y: (ind) => {
										return `${(ind + 1) * 20}%`;
									},
								},
								{ y: 0, duration: 0.6, ease: 'power2.out', stagger: 0.05 }
							);
						},
						onLeaveBack: () => {
							gsap.killTweensOf([...item]);
						},
					});
				});
			}
		}

		return () => {
			if (triggerRef.current.length) {
				triggerRef.current.forEach((item) => item.kill());
			}
			if (groupRef.current?.length) {
				groupRef.current.forEach((element) => {
					element.forEach((el, ind) => {
						gsap.set(el, {
							opacity: 0,
							y: 0,
						});
					});
				});
			}
		};
	}, [pathname, searchParams]);

	return (
		<ListStyle
			ref={styleRef}
			className='list-wrapper'>
			<div className='list-inner-wrapper'>
				<Container row>
					<div className='empty-col col-1 col-ds-12' />

					<div
						ref={recipesRefWrapper}
						className='recipes-wrapper col-10 col-ds-12'>
						<div className='inner-wrapper-filter'>
							<Filter
								selected={selected}
								setSelected={setSelected}
							/>
						</div>

						{data?.length > 0 ? (
							<>
								<div
									ref={recipesWrapRef}
									className='recipes-inner-wrapper'>
									{visibleItems.map((item) => (
										<RecipesItem
											key={item.id}
											{...item}
										/>
									))}
								</div>

								{visibleCount < totalItems ? (
									<div className='show-more-btn-wrapper'>
										<div className='show-more-btn-inner-wrapper'>
											<Button
												onClick={() => handleShowMore()}
												text={'showMore'}
												className={'show-more-btn'}
												btnType={'green-type'}
											/>
										</div>
									</div>
								) : null}
							</>
						) : (
							<div className={'empty-items-wrapper'}>
								<Text
									className={'h4 font-larken-light align-center no-filter-data'}
									text={'noFilterData'}
								/>

								<Button
									text={'clearAllFilter'}
									className={'clear-filter-btn'}
									onClick={handleClearFilters}
								/>
							</div>
						)}
					</div>

					<div className='empty-col col-1 col-ds-12' />
				</Container>
			</div>
		</ListStyle>
	);
};

export default List;
